import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CloseIcon from '@mui/icons-material/Close';
import StopIcon from '@mui/icons-material/Stop';

import { semanticColors } from '@/theme/tokens';
import { VOICE_COUNTDOWN_THRESHOLD_SECONDS } from '@/constants/voice';

/** How many level samples the waveform shows at once. */
const WAVEFORM_BARS = 32;

interface VoiceRecordingBarProps {
  isTranscribing: boolean;
  /** Normalized 0-1 input level. */
  level: number;
  remainingSeconds: number;
  onCancel: () => void;
  onStop: () => void;
  onStopAndSend: () => void;
}

/**
 * The composer's controls row while a voice note is being recorded.
 *
 * Two ways to end a recording, mirroring the mobile app and the dictation
 * pattern voters have met elsewhere: the square stops and drops the transcript
 * into the composer to read first, the arrow stops and sends. Review is the
 * safer default here — a mangled candidate name sends the agent to research
 * the wrong person — so the square carries the neutral fill while the arrow
 * keeps the primary treatment it has when idle.
 */
export function VoiceRecordingBar({
  isTranscribing,
  level,
  remainingSeconds,
  onCancel,
  onStop,
  onStopAndSend,
}: VoiceRecordingBarProps) {
  const { t } = useTranslation('chat');
  const showCountdown = remainingSeconds <= VOICE_COUNTDOWN_THRESHOLD_SECONDS;

  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%' }}
      // Recording state is otherwise carried only by an animation, which is
      // invisible to a screen reader.
      role="status"
      aria-live="polite"
      aria-label={isTranscribing ? t('input.mic.transcribing') : t('input.mic.recording')}
    >
      <IconButton
        onClick={onCancel}
        disabled={isTranscribing}
        aria-label={t('input.mic.cancel')}
        data-testid="voice-cancel-button"
        sx={secondaryButtonSx}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>

      <Box sx={{ flex: 1, minWidth: 0, height: 36, display: 'flex', alignItems: 'center' }}>
        {isTranscribing ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CircularProgress size={16} sx={{ color: semanticColors.textCaption }} />
            <Typography sx={{ fontSize: 13, color: semanticColors.textCaption }}>
              {t('input.mic.transcribing')}
            </Typography>
          </Box>
        ) : (
          <Waveform level={level} />
        )}
      </Box>

      {showCountdown && !isTranscribing && (
        <Typography
          sx={{
            fontSize: 13,
            color: semanticColors.textCaption,
            fontVariantNumeric: 'tabular-nums',
            flexShrink: 0,
          }}
        >
          {t('input.mic.secondsLeft', { seconds: remainingSeconds })}
        </Typography>
      )}

      <IconButton
        onClick={onStop}
        disabled={isTranscribing}
        aria-label={t('input.mic.stop')}
        data-testid="voice-stop-button"
        sx={{
          ...roundButtonSx,
          color: semanticColors.textOnPrimary,
          backgroundColor: semanticColors.textCaption,
          '&:hover': { backgroundColor: semanticColors.textPrimary },
        }}
      >
        <StopIcon sx={{ fontSize: 16 }} />
      </IconButton>

      <IconButton
        onClick={onStopAndSend}
        disabled={isTranscribing}
        aria-label={t('input.mic.send')}
        data-testid="voice-send-button"
        sx={{
          ...roundButtonSx,
          color: semanticColors.textOnPrimary,
          backgroundColor: semanticColors.textPrimary,
          '&:hover': { backgroundColor: semanticColors.brandAccent },
        }}
      >
        <ArrowUpwardIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
}

/**
 * A scrolling bar chart of recent input levels.
 *
 * A rolling buffer rather than a single bouncing meter: the point is feedback
 * that the mic is hearing *you*, and a trace that moves with your voice says
 * that more clearly.
 */
function Waveform({ level }: { level: number }) {
  const [bars, setBars] = useState<number[]>(() => new Array(WAVEFORM_BARS).fill(0));
  // The sampler runs on its own interval, so the latest level reaches it
  // through a ref rather than by resubscribing on every animation frame.
  const levelRef = useRef(level);
  useEffect(() => {
    levelRef.current = level;
  }, [level]);

  useEffect(() => {
    const id = setInterval(() => {
      setBars((prev) => [...prev.slice(1), levelRef.current]);
    }, 100);
    return () => clearInterval(id);
  }, []);

  return (
    <Box
      aria-hidden="true"
      sx={{ display: 'flex', alignItems: 'center', gap: '2px', height: 24, width: '100%' }}
    >
      {bars.map((value, index) => (
        <Box
          key={index}
          sx={{
            flex: 1,
            borderRadius: '1px',
            backgroundColor: semanticColors.textCaption,
            // A visible floor so silence still reads as "listening" rather
            // than "stopped".
            height: `${3 + value * 17}px`,
            opacity: 0.35 + value * 0.65,
            transition: 'height 0.1s linear, opacity 0.1s linear',
          }}
        />
      ))}
    </Box>
  );
}

const roundButtonSx = {
  width: 36,
  height: 36,
  flexShrink: 0,
  transition: 'background-color 0.2s ease, transform 0.1s ease',
  '&:active': { transform: 'scale(0.96)' },
  '&:focus-visible': {
    outline: `2px solid ${semanticColors.brandPrimary}`,
    outlineOffset: '2px',
  },
} as const;

const secondaryButtonSx = {
  ...roundButtonSx,
  color: semanticColors.textCaption,
  backgroundColor: semanticColors.surfaceWarm,
  '&:hover': { backgroundColor: semanticColors.disabledBackground },
} as const;
